import { BasicButton, Loading } from "@/components";
import { OTP } from "@/components/input";
import { axios } from "@/libraries/axios";
import { isAxiosError } from "@/libraries/helper";
import { verification } from "@/redux/reducers/auth";
import { VerificationSearchParamsSchema } from "@/schemas";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

export const Route = createFileRoute("/verification")({
	validateSearch: zodSearchValidator(VerificationSearchParamsSchema),
	loaderDeps: ({ search }) => ({
		token: search.token ?? search.reset_password_token,
	}),
	loader: async ({ deps }) => {
		try {
			const res = await axios.get("/api/v1/auth/password/edit", {
				params: {
					reset_password_token: deps.token,
				},
			});
			return res.data;
		} catch (error) {
			if (isAxiosError(error, 400)) {
				return redirect({
					replace: true,
					to: "/",
					search: {
						message:
							(error.response?.data as any)?.messages ?? "Something went wrong",
					},
				});
			}
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const loaderData = Route.useLoaderData();
	const navigate = Route.useNavigate();
	const dispatch = useDispatch();
	const [verify_code, setVerify_Code] = useState<string>(loaderData?.otp ?? "");
	const verificationError = useSelector(
		(state: any) => state.auth?.Verification.error,
	);
	const [loading, setLoading] = useState(false);

	const handleSubmit = useCallback(() => {
		setLoading(true);
		dispatch(
			verification({
				verify_code: verify_code,
				callback: () => {
					setLoading(false);
					navigate({
						to: "/new-password",
					});
				},
			}),
		);
	}, [dispatch, navigate, verify_code]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (verificationError) {
			loading && setLoading(false);
			toast.error(
				"errors" in verificationError && Array.isArray(verificationError.errors)
					? verificationError.errors.join("\n")
					: (verificationError.messages ?? "Something went wrong!"),
			);
		}
	}, [verificationError]);

	return (
		<>
			<div className="container wrapper">
				<AppBar position="fixed" className="!bg-white !text-dark !shadow-none">
					<Toolbar variant="dense" className="wrapper w-full h-14">
						<Link to={"/forgot-password"}>
							<IconButton
								edge="start"
								color="inherit"
								aria-label="backspace"
								className="mr-2 text-2xl"
							>
								<KeyboardBackspaceIcon className="text-dark" />
							</IconButton>
						</Link>
					</Toolbar>
				</AppBar>
				<Box className="mt-12">
					<h1 className="w-full text-[28px] font-semibold py-3 mb-4">
						Reset password verification code
					</h1>
					<p className="w-full text-sm">
						Kindly key in the verification code received via SMS.
					</p>
					<Box className="max-w-[358px] flex flex-col items-center mx-auto">
						<OTP
							disable={false}
							separator={<span> </span>}
							value={verify_code}
							onChange={setVerify_Code}
							length={4}
						/>
						<BasicButton
							variant="contained"
							color="secondary"
							onClick={handleSubmit}
							disabled={verify_code.length < 4}
							className="w-full h-14 !text-base !shadow-none !mb-2"
						>
							Confirm
						</BasicButton>
					</Box>
					<Link to={"/forgot-password"}>
						<Button
							variant="text"
							className="w-full !h-14 !text-base text-primary !font-semibold text-center !p-0 !mb-2"
						>
							Entered the wrong mobile number?
						</Button>
					</Link>
					<Link to={"/forgot-password"}>
						<Button
							variant="text"
							className="w-full !h-14 !text-base text-primary !font-semibold text-center !p-0 !mb-2"
						>
							Need a new code?
						</Button>
					</Link>
				</Box>
			</div>
			<Loading status={loading} />
		</>
	);
}
